/**
 * Service layer — required parking stall sizes (Supabase `parking_stall_requirements`,
 * database/281, api/parking-stalls.js). Recorded in the zoning research per JURISDICTION, a zone
 * or overlay may override, and the firm holds the fallback; the Modeler resolves a project's sizes
 * through its primary parcel (Project → Parcel → Zone/overlays → Jurisdiction) so each car can draw
 * the city's required stall. See docs/MODELER.md "PARKING STALLS".
 */

import { getParcels, getParcelDetail, zoneDisplayName } from './parcels.js'

export const STALL_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'compact', label: 'Compact' },
  { value: 'garage', label: 'Enclosed garage' },
  { value: 'accessible', label: 'Accessible (ADA)' },
]

const getFirst = (v) => (Array.isArray(v) ? v[0] : v)

async function post(payload) {
  const headers = { 'Content-Type': 'application/json' }
  // a panel mounted at page load can ask before the app has published its sign-in
  // token — wait briefly for it rather than sending an unauthenticated request
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken !== 'function') {
    for (let i = 0; i < 40 && typeof window.__clerkGetToken !== 'function'; i++) {
      await new Promise((r) => setTimeout(r, 250))
    }
  }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/parking-stalls', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/parking-stalls' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** the rows recorded on ONE scope ('firm' | 'jurisdiction' | 'zone'), keyed by stall type */
export async function listStallSizes(scope, scopeId = null) {
  const { rows } = await post({ action: 'list', scope, scopeId })
  return Object.fromEntries((rows || []).map((r) => [r.stallType, r]))
}

/** record (or, with a blank width/length, clear) one stall type's size on a scope */
export async function setStallSize(scope, scopeId, stallType, { widthFt, lengthFt, aisleFt, codeReference, notes }, byName) {
  const { row } = await post({ action: 'set', scope, scopeId, stallType, widthFt, lengthFt, aisleFt, codeReference, notes, byName })
  return row
}

/**
 * A project's required stall sizes, resolved most-specific-first (overlay zones → base zone →
 * jurisdiction → firm fallback). Never throws for missing research — a project with no parcel
 * (or a sandbox, projectId null) resolves to the firm fallback with `jurisdictionName: null`.
 * @returns {Promise<{ stalls: Record<string, {widthFt, lengthFt, aisleFt, codeReference, source, sourceId}>,
 *                     jurisdictionName: string|null, zoneName: string|null }>}
 */
export async function resolveProjectStallSizes(projectId) {
  let zoneIds = [], jurisdictionId = null, jurisdictionName = null, zoneName = null
  if (projectId) {
    try {
      const parcels = await getParcels(projectId)
      const first = parcels?.[0]
      if (first) {
        const d = await getParcelDetail(first.id)
        const overlays = (d.overlays || []).map((o) => o.id)
        zoneIds = [...overlays, ...(d.zone ? [d.zone.id] : [])]
        jurisdictionId = d.jurisdiction?.id || getFirst(d.parcel?.fields?.Jurisdictions) || null
        jurisdictionName = d.jurisdiction?.fields?.Name || null
        zoneName = d.zone ? zoneDisplayName(d.zone.fields) : null
      }
    } catch { /* no research reachable — fall through to the firm fallback */ }
  }
  const { stalls } = await post({ action: 'resolve', zoneIds, jurisdictionId })
  return { stalls: stalls || {}, jurisdictionName, zoneName }
}
